<template>
    <BaseCard class="relative flex flex-col gap-2 overflow-hidden text-ink-muted" :class="containerClass">
        <BaseButton
            v-if="!assessmentEnabled && !perspectiveReport"
            theme="tertiary"
            icon-only
            size="sm"
            class="absolute top-4 right-4"
            :disabled="isFetching"
            title="Refresh Perspective Report"
            @click="$emit('refresh')">
            <i class="bi bi-arrow-clockwise" :class="{ 'animate-spin': isFetching }" aria-hidden="true" />
        </BaseButton>
        <div class="shrink-0">
            <ArchetypeDot v-if="perspectiveArchetype" :letters="perspectiveArchetype.letters" class="size-14" />
            <PerspectiveCircles v-else-if="hasPerspectiveResult" />
            <PerspectiveStyleCircles v-else />
        </div>
        <div class="flex flex-col w-full" :class="{ 'items-center': archetype }">
            <div v-if="archetype" class="details-big text-ink-muted">You're {{ articleFor(archetype) }}</div>
            <div class="text-ink" :class="titleClass">
                {{ cardTitle }}
            </div>
            <div class="text-ink-muted" :class="hasPerspectiveResult ? 'details-big mt-1' : 'body text-pretty'">
                {{ cardDescription }}
            </div>
        </div>
        <BaseButton theme="neutral" class="w-full" :class="{ 'mt-auto': hasPerspectiveResult }" @click="handleClick">{{ ctaButtonText }}</BaseButton>
    </BaseCard>
</template>

<script setup lang="ts">
import { usePage } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import { type PerspectiveReport } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import PerspectiveCircles from "~vue/components/PerspectiveCircles.vue";
import PerspectiveStyleCircles from "~vue/components/PerspectiveStyleCircles.vue";
import { PERSPECTIVE_TYPES } from "~vue/pages/perspectiveTypes";
import ArchetypeDot from "~vue/Perspective/ArchetypeDot.vue";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { articleFor } from "~vue/utils/stringUtils";
import { computed, onMounted, ref } from "vue";

interface Props {
    perspectiveReport?: PerspectiveReport | null;
    isFetching?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    perspectiveReport: null,
    isFetching: false,
});

defineEmits<{ refresh: [] }>();

const perspectiveReport = computed(() => props.perspectiveReport);

const page = usePage();
const assessmentEnabled = computed(() => !!page.props.perspective_assessment_enabled);
const assessmentCompleted = computed(() => !!page.props.perspective_assessment_completed);
const assessmentInProgress = computed(() => !!page.props.perspective_assessment_in_progress);
const hasPerspectiveResult = computed(() => assessmentCompleted.value || !!perspectiveReport.value || !!perspectiveArchetype.value);

const perspectivePath = computed(() => (assessmentEnabled.value ? "native" : "team_tools"));
const resumeState = computed(() => (assessmentEnabled.value && assessmentInProgress.value ? "resume" : "fresh"));
const eventProps = computed(() => ({
    has_report: hasPerspectiveResult.value,
    surface: "home_discover_bar",
    perspective_path: perspectivePath.value,
    resume_state: resumeState.value,
}));

onMounted(() => logUserInteraction(AmplitudeEventNames.PERSPECTIVE_CARD_SHOWN, eventProps.value));

const perspectiveArchetype = computed(() => page.props.perspective_archetype as { letters: string; title: string } | null);
const archetype = computed(() => perspectiveArchetype.value?.title ?? undefined);
const typeData = computed(() => {
    const letters = perspectiveArchetype.value?.letters;
    return letters ? (PERSPECTIVE_TYPES[letters] ?? null) : null;
});

const titleClass = computed(() => {
    if (archetype.value) {
        return "display-serif";
    }
    return hasPerspectiveResult.value ? "nadia-subtitle" : "title-body";
});

const containerClass = computed(() => {
    if (archetype.value) {
        return "items-center";
    }
    return hasPerspectiveResult.value ? "items-start" : "items-start justify-end min-h-[305px]";
});

const cardTitle = computed(() => {
    if (archetype.value) {
        return archetype.value;
    }
    return hasPerspectiveResult.value ? "Your Archetype" : "Discover your working style";
});
const cardDescription = computed(() => {
    if (typeData.value) {
        return typeData.value.shortDescription;
    }
    if (hasPerspectiveResult.value) {
        return "Chat with Nadia to get personalized insights for your working style.";
    }
    return "Take your Perspective to see which one of 16 working styles fits you — and what it reveals about how you work.";
});

const perspectiveUrl = computed(() => {
    if (assessmentEnabled.value) {
        return assessmentCompleted.value ? "/perspective/results/" : "/perspective/start/";
    }
    return props.perspectiveReport ? "/coach/start/?script=perspective&chat_source=perspective_topic" : `${page.props.align_host}/perspective/questions`;
});

const ctaButtonText = computed(() => (hasPerspectiveResult.value ? "Dive Deeper" : "Discover my style"));

const dismissPayload = ref<{ interaction_type: string } | null>(null);
const { execute: saveDismissal } = useFetch("/api/accounts/ui-interactions", { immediate: false }).post(dismissPayload).json();

const handleClick = () => {
    logUserInteraction(AmplitudeEventNames.PERSPECTIVE_CARD_CLICKED, eventProps.value);

    if (hasPerspectiveResult.value) {
        dismissPayload.value = { interaction_type: "perspective_card_dismissed" };
        saveDismissal();
    } else if (assessmentEnabled.value && !assessmentInProgress.value) {
        logUserInteraction(AmplitudeEventNames.PERSPECTIVE_ASSESSMENT_STARTED, { assessment_type: "perspective", source: "home_discover_bar" });
    }

    if (assessmentEnabled.value || props.perspectiveReport) {
        openUrl(perspectiveUrl.value);
    } else {
        // "Complete" - open in new tab (external link to Team Toolkit)
        openUrl(perspectiveUrl.value, { target: "_blank" });
    }
};
</script>
